import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { keycloak } from "../Components/Common/functions/keycloakConfig";
import { useDispatch, useSelector } from "react-redux";
import { getUserByName } from "../slices/UserManagement/GetUserByName/thunk";
import { hasPermission } from "../Components/Utils/UtilityFunctions";
import { fetchPermissions } from "../permissionsSlice";

const KeycloakCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasFetchedUser = useRef(false);
  const hasFetchedPermissions = useRef(false);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [error, setError] = useState(null);
  const [authReady, setAuthReady] = useState(false);

  const initialValues = useMemo(() => ({
    firstname: "",
    lastname: "",
    username: "",
    userrole: "",
    phone: "",
    email: "",
    status: ""
  }), []);

  useEffect(() => {
    const environment = localStorage.getItem('environment');

    if (!environment || (environment?.toLowerCase() !== 'keycloak' && environment?.toLowerCase() !== 'azure' && environment?.toLowerCase() !== 'google')) {
      console.error('Invalid environment for Keycloak callback:', environment);
      setError('Invalid authentication environment');
      setTimeout(() => navigate("/signin", { replace: true }), 2000);
      return;
    }
  }, [navigate]);

  // Poll for Keycloak authentication
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const error = urlParams.get("error");
    const errorDescription = urlParams.get("error_description");
    const hasCode = urlParams.get("code");

    if (error) {
      console.error('OAuth error:', error, errorDescription);
      setError(errorDescription || "Authentication failed");
      setTimeout(() => navigate("/signin", { replace: true }), 3000);
      return;
    }

    if (!hasCode) {
      console.error('No authorization code in URL');
      setError("Missing authorization code");
      setTimeout(() => navigate("/signin", { replace: true }), 2000);
      return;
    }

    let attempts = 0;
    const maxAttempts = 100; // 10 seconds total

    const checkInterval = setInterval(() => {
      attempts++;

      // Check if we have a token in localStorage (set by index.js after Keycloak init)
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");

      const isReady = keycloak.authenticated && keycloak.token && token && userId;

      if (isReady) {
        clearInterval(checkInterval);
        setAuthReady(true);
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
        console.error('Authentication timeout after', attempts * 100, 'ms');
        setError("Authentication timeout - please try again");
        setTimeout(() => navigate("/signin", { replace: true }), 2000);
      }
    }, 100);

    return () => clearInterval(checkInterval);
  }, [location, navigate]);

  // Fetch user data
  useEffect(() => {
    if (!authReady || hasFetchedUser.current || error) return;

    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("token");

    if (!userId || !token) {
      console.error('Missing userId or token');
      setError("Session data missing");
      setTimeout(() => navigate("/signin", { replace: true }), 2000);
      return;
    }

    hasFetchedUser.current = true;
    dispatch(getUserByName(userId, setLoading, initialValues, setEditData));
  }, [authReady, dispatch, initialValues, error, navigate]);

  // Fetch permissions
  useEffect(() => {
    if (!authReady || hasFetchedPermissions.current || error) return;

    const userId = localStorage.getItem("userId");
    const token = localStorage.getItem("token");

    if (!userId || !token) {
      return;
    }

    hasFetchedPermissions.current = true;
    dispatch(fetchPermissions());
  }, [authReady, dispatch, error]);

  const user = useSelector((state) => state.GetAllUserByName?.getUsersByName);
  const rawPermissions = useSelector((state) => state.permissions.permissionsMap);
  const permissions = useSelector((state) => state.permissions.permissions);

  useEffect(() => {
    if (permissions?.modules) {
      localStorage.setItem("permissionss", JSON.stringify(permissions.modules));
    }
  }, [permissions]);

  const permissionsMap = useMemo(() => {
    if (!rawPermissions || rawPermissions.length === 0) {
      return new Map();
    }
    return new Map(rawPermissions.map(([key, value]) => [key, new Set(value)]));
  }, [rawPermissions]);

  // Navigate when everything is ready
  useEffect(() => {
    if (!authReady || error || loading) return;

    const currentUser = editData || user;

    const hasValidUser = currentUser &&
      !Array.isArray(currentUser) &&
      typeof currentUser === 'object' &&
      Object.keys(currentUser).length > 0;

    const hasValidPermissions = permissionsMap.size > 0;

    if (!hasValidUser || !hasValidPermissions) {
      return;
    }

    // Store user data
    const roleName = currentUser.role;
    const userName = currentUser.userDetails?.userName;
    const operatorId = currentUser.operatorId;
    const operatorName = currentUser.operatorName;
    const customerId = currentUser.customerId;

    localStorage.setItem("roleName", roleName);
    localStorage.setItem("userName", userName);
    localStorage.setItem("operatorId", operatorId);
    localStorage.setItem("operatorName", operatorName);
    localStorage.setItem("customerId", customerId);

    if (roleName === 'ISPPartnerAdmin' && operatorId) {
      const partnerId = operatorId.split('/').pop();
      localStorage.setItem('PartnerId', partnerId);
    }
    localStorage.setItem('dateFormat', "DD MMM YYYY");

    // Determine target route based on permissions
    const hasDashboardPermission = hasPermission(permissionsMap, "dashboard", "dashboard");
    const hasCustomerPermission = hasPermission(permissionsMap, "customer", "customer");

    let targetRoute = '/operator-listing';

    if (hasDashboardPermission) {
      targetRoute = '/dashboard';
    } else if (hasCustomerPermission) {
      targetRoute = '/customer';
    }
    navigate(targetRoute, { replace: true });
  }, [user, editData, permissionsMap, authReady, loading, error, navigate]);

  return null;
};

export default KeycloakCallback;